import React, { useState } from 'react';

import { DateTime } from 'luxon';

import { Alert, Breadcrumb, Panel } from '@shared/components/bootstrap';

import { PageHeader } from '@admin/components/helpers/page_header';
import { Pagination } from '@admin/components/pagination';
import { Spinner } from '@admin/components/spinner';
import { jobAvailabilityNewURL, regionOperatingScheduleTemplateNewURL } from '@admin/config/routes';
import { usePermission } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { AVAILABILITY_ADMIN } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  Availability__BlockReasonEnum,
  Availability__JobAvailabilityFiltersInput,
  AvailabilityJobAvailabilityDocument,
  useAvailabilityJobAvailabilitiesDestroyMutation,
  useAvailabilityJobAvailabilityQuery,
} from '@admin/schema';

import { AvailabilityControls } from '../controls';
import { Option } from '../indeterminate_checkbox_form_group';
import { Filters } from './filters';
import { Entries } from './list/entries';

const DEFAULT_FILTERS: Availability__JobAvailabilityFiltersInput = {
  fromDate: DateTime.local().toISODate(),
  tillDate: DateTime.local().plus({ days: 30 }).toISODate(),
  kinds: [],
  regionIDs: [],
};

export const List: React.FC = () => {
  useTitle('Job Availability');
  const [filters, setFilters] = useState<Availability__JobAvailabilityFiltersInput>(DEFAULT_FILTERS);
  const [page, setPage] = useState<number>();
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);

  const { data, loading: querying } = useAvailabilityJobAvailabilityQuery({
    client,
    variables: {
      page,
      filters,
    },
    fetchPolicy: 'network-only',
  });

  const [destroy, { data: destroyData, loading: destroying }] = useAvailabilityJobAvailabilitiesDestroyMutation({
    client,
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: AvailabilityJobAvailabilityDocument,
        variables: {
          filters,
        },
      },
    ],
    onCompleted: () => setSelectedOptions([]),
  });

  const onDestroy = async () => {
    await destroy({
      variables: {
        input: {
          jobTypeAvailabilityIDs: selectedOptions
            .filter(({ __typename }) => __typename === 'Availability__JobTypeAvailability')
            .map(({ id }) => id),
        },
      },
    });
  };

  const onPageChange = (pageNumber: number) => {
    setSelectedOptions([]);
    setPage(pageNumber);
  };

  const canModify = usePermission(AVAILABILITY_ADMIN.resource, AVAILABILITY_ADMIN.actions.update);
  const paginated = data && data.paginated;
  const options =
    paginated?.results
      .filter((result) => result.reason !== Availability__BlockReasonEnum.WeeklySchedule)
      .map(({ __typename, id }) => ({ __typename: __typename!, id })) ?? [];
  const loading = querying || destroying;

  return (
    <>
      <PageHeader>
        <Breadcrumb>
          <Breadcrumb.Item active>Job Availability</Breadcrumb.Item>
        </Breadcrumb>
      </PageHeader>
      <Panel>
        <Panel.Body>
          {destroyData?.result?.error && <Alert style="danger">{destroyData.result.error}</Alert>}
          <Filters filters={filters} onChange={setFilters} />
          <AvailabilityControls
            createHref={jobAvailabilityNewURL()}
            weeklyScheduleHref={regionOperatingScheduleTemplateNewURL()}
            loading={loading}
            options={options}
            selectedOptions={selectedOptions}
            create={canModify}
            destroy={canModify}
            onDestroy={onDestroy}
            setSelectedOptions={setSelectedOptions}
          />
          {paginated && (
            <Entries
              availabilities={paginated.results}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
            />
          )}
          {loading && <Spinner />}
          {paginated && <Pagination onPage={onPageChange} pagination={paginated.pagination} />}
        </Panel.Body>
      </Panel>
    </>
  );
};
